var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, } from 'react';
import { DatePicker, Flex, Form, InputNumber, Radio, Select, Typography, WeekdayPicker, } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import { recurrencePanelReducer, shouldBumpConversionId } from './panelReducer';
import momentGenerateConfig from '@rc-component/picker/generate/moment';
import { clearPresetWhenAbtClears, computeMaxOccurrencesUnder, enforceNeverEndingUnderAbt, getByWeekday, getDefaultPanelState, intervalToFrequency, panelToRRule, reconcileAbtDateChange, rruleToPanelState, selectPreset, } from './utils';
var MomentDatePicker = DatePicker.generatePicker(momentGenerateConfig);
export var RecurrenceDropdown = function (_a) {
    var onChange = _a.onChange, dateStart = _a.dateStart, _b = _a.savedRRule, savedRRule = _b === void 0 ? null : _b, disabled = _a.disabled, _c = _a.abtUntilDate, abtUntilDate = _c === void 0 ? null : _c, _d = _a.hasAbtConflict, hasAbtConflict = _d === void 0 ? false : _d, _e = _a.recurrenceEndsBeforeStart, recurrenceEndsBeforeStart = _e === void 0 ? false : _e;
    var _f = useTranslation('meetingRooms'), t = _f.t, i18n = _f.i18n;
    var localizedAbtUntilDate = useMemo(function () {
        if (!abtUntilDate)
            return '';
        return abtUntilDate.clone().locale(i18n.language).format('LL');
    }, [abtUntilDate, i18n.language]);
    var _g = __read(useState(function () {
        if (savedRRule) {
            return rruleToPanelState(savedRRule, dateStart, abtUntilDate);
        }
        return getDefaultPanelState(abtUntilDate);
    }), 1), initialPanelState = _g[0];
    var _h = __read(useReducer(recurrencePanelReducer, initialPanelState), 2), panel = _h[0], dispatchPanelAction = _h[1];
    var _j = __read(useState(0), 2), conversionId = _j[0], setConversionId = _j[1];
    var applyPanel = useCallback(function (nextPanel, bumpConversion) {
        if (bumpConversion === void 0) { bumpConversion = true; }
        dispatchPanelAction({ type: 'HYDRATE', panel: nextPanel });
        if (bumpConversion) {
            setConversionId(function (id) { return id + 1; });
        }
    }, []);
    var setPreset = useCallback(function (value) {
        var next = selectPreset(panel, value, dateStart, abtUntilDate);
        if (shouldBumpConversionId(panel, next)) {
            setConversionId(function (id) { return id + 1; });
        }
        dispatchPanelAction({
            type: 'SET_PRESET',
            value: value,
            dateStart: dateStart,
            abtUntilDate: abtUntilDate,
        });
    }, [panel, dateStart, abtUntilDate]);
    var patchCustom = useCallback(function (patch) {
        dispatchPanelAction({
            type: 'PATCH_CUSTOM',
            patch: patch,
        });
    }, []);
    var recurrenceRule = panel.recurrenceRule, presetUnderAbt = panel.presetUnderAbt, interval = panel.interval, intervalValue = panel.intervalValue, endOption = panel.endOption, endDate = panel.endDate, occurrenceCount = panel.occurrenceCount, selectedWeekdays = panel.selectedWeekdays;
    var byweekday = useMemo(function () { return getByWeekday(recurrenceRule, selectedWeekdays, dateStart); }, [recurrenceRule, selectedWeekdays, dateStart]);
    var rrule = useMemo(function () { return panelToRRule(panel, dateStart, byweekday); }, [panel, dateStart, byweekday]);
    var maxOccurrencesUnderAbt = useMemo(function () {
        if (!abtUntilDate || recurrenceRule !== 'CUSTOM') {
            return undefined;
        }
        return computeMaxOccurrencesUnder({
            freq: intervalToFrequency(interval),
            interval: intervalValue,
            byweekday: byweekday,
            dateStart: dateStart,
            until: abtUntilDate,
        });
    }, [
        abtUntilDate,
        recurrenceRule,
        interval,
        intervalValue,
        byweekday,
        dateStart,
    ]);
    var onChangeRef = useRef(onChange);
    useEffect(function () {
        onChangeRef.current = onChange;
    }, [onChange]);
    // Distinguishes async edit hydration (form rule arrives after mount) from the
    // user choosing "Does not repeat" — both yield a null local rrule while the
    // form may still hold the previous rule until onChange(null) propagates.
    var hadLocalRRuleRef = useRef(!!savedRRule);
    useEffect(function () {
        var _a, _b, _c;
        var formKey = (_a = savedRRule === null || savedRRule === void 0 ? void 0 : savedRRule.toString()) !== null && _a !== void 0 ? _a : null;
        var localKey = (_b = rrule === null || rrule === void 0 ? void 0 : rrule.toString()) !== null && _b !== void 0 ? _b : null;
        if (localKey) {
            hadLocalRRuleRef.current = true;
        }
        if (formKey === localKey) {
            return;
        }
        if (formKey && !localKey) {
            if (hadLocalRRuleRef.current) {
                (_c = onChangeRef.current) === null || _c === void 0 ? void 0 : _c.call(onChangeRef, null);
                return;
            }
            if (savedRRule) {
                applyPanel(rruleToPanelState(savedRRule, dateStart, abtUntilDate));
            }
            return;
        }
        if (localKey && onChangeRef.current) {
            onChangeRef.current(rrule);
        }
    }, [rrule, savedRRule, dateStart, abtUntilDate, applyPanel]);
    var isFirstAbtSpaceSyncRef = useRef(true);
    var prevAbtUntilDateRef = useRef(null);
    useEffect(function () {
        var cleared = clearPresetWhenAbtClears(panel, abtUntilDate);
        if (cleared) {
            dispatchPanelAction({ type: 'HYDRATE', panel: cleared });
            if (!abtUntilDate) {
                prevAbtUntilDateRef.current = null;
            }
            return;
        }
        if (!abtUntilDate) {
            prevAbtUntilDateRef.current = null;
            return;
        }
        if (panel.recurrenceRule === 'DO_NOT_REPEAT') {
            if (isFirstAbtSpaceSyncRef.current) {
                isFirstAbtSpaceSyncRef.current = false;
            }
            prevAbtUntilDateRef.current = abtUntilDate.clone();
            return;
        }
        var ruleToFix = rrule !== null && rrule !== void 0 ? rrule : savedRRule;
        var nextPanel = null;
        if (ruleToFix) {
            nextPanel = enforceNeverEndingUnderAbt(panel, ruleToFix, dateStart, abtUntilDate);
        }
        if (!nextPanel && !isFirstAbtSpaceSyncRef.current) {
            nextPanel = reconcileAbtDateChange(panel, abtUntilDate, prevAbtUntilDateRef.current, dateStart, ruleToFix, maxOccurrencesUnderAbt);
        }
        if (isFirstAbtSpaceSyncRef.current) {
            isFirstAbtSpaceSyncRef.current = false;
        }
        prevAbtUntilDateRef.current = abtUntilDate.clone();
        if (nextPanel) {
            applyPanel(nextPanel, shouldBumpConversionId(panel, nextPanel));
        }
    }, [
        abtUntilDate,
        panel,
        rrule,
        savedRRule,
        dateStart,
        maxOccurrencesUnderAbt,
        applyPanel,
    ]);
    var dayName = useMemo(function () { return dateStart.format('dddd'); }, [dateStart]);
    var recurrenceOptions = useMemo(function () {
        return [
            {
                value: 'DO_NOT_REPEAT',
                label: t('eventDetails.recurrence.options.does_not_repeat'),
            },
            { value: 'DAILY', label: t('eventDetails.recurrence.options.daily') },
            { value: 'WEEKLY', label: t('eventDetails.recurrence.options.weekly') },
            {
                value: 'MONTHLY',
                label: t('eventDetails.recurrence.options.monthly'),
            },
            {
                value: 'WEEKDAY',
                label: t('eventDetails.recurrence.options.weekday'),
            },
            { value: 'MOWEFR', label: t('eventDetails.recurrence.options.mowefr') },
            { value: 'TUTH', label: t('eventDetails.recurrence.options.tuth') },
            {
                value: 'WEEKLY_ON_DAY',
                label: t('eventDetails.recurrence.options.weekly_on_day', {
                    dayName: dayName,
                }),
            },
            {
                value: 'MONTHLY_ON_DAY',
                label: t('eventDetails.recurrence.options.monthly_on_day'),
            },
            { value: 'CUSTOM', label: t('eventDetails.recurrence.options.custom') },
        ];
    }, [t, dayName]);
    var intervalOptions = useMemo(function () {
        return [
            { value: 'DAY', label: t('eventDetails.recurrence.intervals.day') },
            { value: 'WEEK', label: t('eventDetails.recurrence.intervals.week') },
            {
                value: 'MONTHLY',
                label: t('eventDetails.recurrence.intervals.month'),
            },
        ];
    }, [t]);
    return (_jsxs(Flex, { vertical: true, gap: 8, children: [_jsx(Form.Item, { label: t('eventDetails.recurrence.label'), layout: "vertical", style: { marginBottom: 0 }, children: _jsx(Select, { value: presetUnderAbt !== null && presetUnderAbt !== void 0 ? presetUnderAbt : recurrenceRule, onChange: setPreset, options: recurrenceOptions, disabled: disabled, status: hasAbtConflict || recurrenceEndsBeforeStart ? 'error' : undefined }) }), abtUntilDate && recurrenceRule !== 'DO_NOT_REPEAT' && (_jsx(Typography.Text, { type: "secondary", children: t('eventDetails.recurrence.advanced_booking_threshold_subtext', {
                    date: localizedAbtUntilDate,
                }) })), recurrenceEndsBeforeStart && recurrenceRule !== 'DO_NOT_REPEAT' && (_jsx(Typography.Text, { type: "danger", children: t('eventDetails.recurrence.ends_before_start') })), hasAbtConflict &&
                !recurrenceEndsBeforeStart &&
                recurrenceRule !== 'DO_NOT_REPEAT' && (_jsx(Typography.Text, { type: "danger", children: t('eventDetails.recurrence.advanced_booking_threshold_conflict') })), recurrenceRule === 'CUSTOM' && (_jsxs(_Fragment, { children: [_jsx(Form.Item, { label: t('eventDetails.recurrence.repeat_every'), layout: "vertical", style: { marginBottom: 0 }, children: _jsxs(Flex, { gap: 4, children: [_jsx(InputNumber, { step: 1, value: intervalValue, onChange: function (value) {
                                        patchCustom({ intervalValue: value || 1 });
                                    } }), _jsx(Select, { value: interval, onChange: function (value) {
                                        patchCustom({ interval: value });
                                    }, options: intervalOptions })] }) }), _jsx(Form.Item, { label: t('eventDetails.recurrence.repeat_on'), layout: "vertical", style: { marginBottom: 0 }, children: _jsx(WeekdayPicker, { selectedDays: selectedWeekdays, onChange: function (days) {
                                patchCustom({ selectedWeekdays: days });
                            } }, conversionId) }), _jsx(Form.Item, { label: t('eventDetails.recurrence.ends'), layout: "vertical", style: { marginBottom: 0 }, children: _jsxs(Radio.Group, { onChange: function (e) {
                                patchCustom({ endOption: e.target.value });
                            }, value: endOption, style: { display: 'flex', flexDirection: 'column', gap: 8 }, children: [!abtUntilDate && (_jsx(Radio, { value: "never", children: t('eventDetails.recurrence.end_options.never') })), _jsxs(Flex, { gap: "small", align: "center", children: [_jsx(Radio, { value: "onDate", children: t('eventDetails.recurrence.end_options.on') }), _jsx(MomentDatePicker, { value: endDate, onChange: function (date) {
                                                patchCustom({ endDate: date, endOption: 'onDate' });
                                            }, allowClear: false, minDate: dateStart, maxDate: abtUntilDate !== null && abtUntilDate !== void 0 ? abtUntilDate : undefined })] }), _jsxs(Flex, { gap: "small", align: "center", children: [_jsx(Radio, { value: "afterOccurrences", children: t('eventDetails.recurrence.end_options.after') }), _jsx(InputNumber, { step: 1, min: 1, max: maxOccurrencesUnderAbt, value: occurrenceCount, onChange: function (value) {
                                                patchCustom({
                                                    occurrenceCount: value || 1,
                                                    endOption: 'afterOccurrences',
                                                });
                                            } }), _jsx(Typography.Text, { children: t('eventDetails.recurrence.end_options.occurrences') })] })] }) })] }))] }));
};
